import React, { useEffect, useMemo, useState } from 'react';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { School } from 'models/school';
import { Tournament } from 'models/tournament';
import { getAllSchoolUnionTournaments } from 'services/schoolUnion/tournaments';
import { getAllSchoolsIncludedInUnion } from 'services/schoolUnion/schools';
import Loader from 'components/loader';
import './Venues.scss';

interface Props { school: School; }
interface Venue { key: string; name: string; postcode: string; tournaments: number; point?: { coordinates: [number, number] }; }

export default function Venues({ school }: Props) {
   const [items, setItems] = useState<Tournament[]>([]);
   const [query, setQuery] = useState('');
   const [loading, setLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);

   useEffect(() => {
      let active = true;
      setLoading(true);
      setLoadError(false);
      getAllSchoolsIncludedInUnion(school.id)
         .then(included => getAllSchoolUnionTournaments([school.id, ...included.filter(item => item.kind === 'SchoolUnion').map(item => item.id)]))
         .then(next => { if (active) setItems(next || []); })
         .catch(() => { if (active) { setItems([]); setLoadError(true); } })
         .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
   }, [school.id]);

   const venues = useMemo(() => {
      const byVenue = new Map<string, Venue>();
      items.forEach(item => {
         const venue = item.venue as any;
         if (!venue || venue.venueType === 'TBD') return;
         const name = venue.placeData?.name || item.school?.name || 'Tournament venue';
         const postcode = venue.postcodeData?.postcode || venue.postcode || '';
         const key = venue.placeId || `${name}|${postcode}`.toLowerCase();
         const current = byVenue.get(key);
         byVenue.set(key, { key, name, postcode, point: venue.point, tournaments: (current?.tournaments || 0) + 1 });
      });
      return [...byVenue.values()].sort((a, b) => a.name.localeCompare(b.name));
   }, [items]);

   const visible = venues.filter(item => `${item.name} ${item.postcode}`.toLowerCase().includes(query.trim().toLowerCase()));
   if (loading) return <Loader />;

   return <main className='union-venues'>
      <header><div><span>Where we play</span><h1>Venues</h1><p>Locations hosting {school.name} tournaments and competitions.</p></div><strong>{venues.length} locations</strong></header>
      {venues.length > 0 && <label className='union-venues__search'><SearchRoundedIcon /><input value={query} onChange={event => setQuery(event.target.value)} placeholder='Search venue or postcode' /></label>}
      {!loadError && visible.length > 0 ? <div className='union-venues__grid'>{visible.map(item => {
         const coordinates = item.point?.coordinates;
         const mapLink = coordinates ? `https://www.google.com/maps?q=${coordinates[1]},${coordinates[0]}` : `https://www.google.com/maps/search/${encodeURIComponent(`${item.name} ${item.postcode}`)}`;
         return <article key={item.key}><div className='union-venues__pin'><LocationOnRoundedIcon /></div><div><h2>{item.name}</h2><p>{item.postcode || 'Address details to be confirmed'}</p><span>{item.tournaments} {item.tournaments === 1 ? 'tournament' : 'tournaments'}</span></div><a href={mapLink} target='_blank' rel='noreferrer'>View map</a></article>;
      })}</div> : <section className='union-venues__empty' role={loadError ? 'alert' : undefined}><LocationOnRoundedIcon /><h2>{loadError ? 'Venues could not be loaded' : venues.length ? 'No matching venues' : 'No venues published yet'}</h2><p>{loadError ? 'Please refresh the page and try again.' : venues.length ? 'Try another venue name or postcode.' : 'Tournament venues will appear here when they are published.'}</p></section>}
   </main>;
}
